import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Settings as SettingsIcon, User, Key, Globe, Copy, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SettingsPage = () => {
  const { user, client, refreshUser } = useAuth();
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const handlePasswordChange = async () => {
    if (!passwordData.current || !passwordData.new) {
      toast.error('Current and new password are required');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/auth/password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      setPasswordData({ current: '', new: '', confirm: '' });
      toast.success('Password updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const webhookUrl = client ? `${process.env.REACT_APP_BACKEND_URL}/api/webhook/${client.id}` : '';

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-[30px] font-semibold text-[#131517]">Settings</h1>
        <p className="text-[14px] text-[#595C5C] mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="border-[#D9E2E5]">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#131517] rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-[18px] font-semibold text-[#131517]">Profile</CardTitle>
              <CardDescription className="text-[#8A8A8B]">Your account information</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Name</Label>
              <p className="text-[14px] text-[#131517] mt-1">{user?.name}</p>
            </div>
            <div>
              <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Email</Label>
              <p className="text-[14px] text-[#131517] mt-1">{user?.email}</p>
            </div>
            <div>
              <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Role</Label>
              <p className="text-[14px] text-[#131517] mt-1 capitalize">{user?.role?.toLowerCase().replace('_', ' ')}</p>
            </div>
            {client && (
              <div>
                <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Organization</Label>
                <p className="text-[14px] text-[#131517] mt-1">{client.name}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Password Card */}
      <Card className="border-[#D9E2E5]">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#131517] rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-[18px] font-semibold text-[#131517]">Change Password</CardTitle>
              <CardDescription className="text-[#8A8A8B]">Update your account password</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#131517]">Current Password</Label>
            <Input
              type="password"
              value={passwordData.current}
              onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
              placeholder="Enter current password"
              className="border-[#D9E2E5] max-w-md"
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#131517]">New Password</Label>
            <Input
              type="password"
              value={passwordData.new}
              onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
              placeholder="Enter new password"
              className="border-[#D9E2E5] max-w-md"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#131517]">Confirm New Password</Label>
            <Input
              type="password"
              value={passwordData.confirm}
              onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
              placeholder="Confirm new password"
              className="border-[#D9E2E5] max-w-md"
              data-testid="confirm-password-input"
            />
          </div>
          <Button 
            onClick={handlePasswordChange} 
            disabled={saving}
            className="bg-[#131517] hover:bg-[#333537]"
            data-testid="change-password-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Update Password
          </Button>
        </CardContent>
      </Card>

      {/* Webhook Configuration */}
      {client && (
        <Card className="border-[#D9E2E5]">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#131517] rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-[18px] font-semibold text-[#131517]">Webhook Configuration</CardTitle>
                <CardDescription className="text-[#8A8A8B]">Configuration for your third-party integration</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Webhook URL</Label>
              <div className="flex gap-2">
                <code className="flex-1 bg-[#EFEFEF] px-3 py-2 rounded-lg text-[13px] text-[#131517] break-all">
                  {webhookUrl}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(webhookUrl)}
                  className="border-[#D9E2E5] px-3"
                  data-testid="copy-webhook-url"
                >
                  {copied ? <Check className="w-4 h-4 text-[#3BAB59]" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Webhook Token</Label>
              <div className="flex gap-2">
                <code className="flex-1 bg-[#EFEFEF] px-3 py-2 rounded-lg text-[13px] text-[#131517] break-all">
                  {client.webhook_token}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(client.webhook_token)}
                  className="border-[#D9E2E5] px-3"
                  data-testid="copy-webhook-token"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <Separator className="bg-[#D9E2E5]" />
            {/* Mirror Send instructions */}
            <div className="space-y-3">
              <div>
                <p className="text-[13px] font-semibold text-[#131517] mb-1">Inbox Content Mirror (for third-party senders)</p>
                <p className="text-[13px] text-[#595C5C]">
                  If you send WhatsApp messages from an external tool using this same WhatsApp number, you can forward those send payloads to this same Webhook URL so the actual message content appears in the inbox instead of a generic placeholder.
                </p>
              </div>
              <div className="bg-[#F8F9FA] rounded-lg p-3 border border-[#D9E2E5] space-y-2">
                <p className="text-[12px] font-semibold text-[#595C5C] uppercase">How it works</p>
                <ol className="text-[12px] text-[#595C5C] list-decimal list-inside space-y-1">
                  <li>Send your message to Meta's API as usual — save the <code className="bg-[#EFEFEF] px-1 rounded">wamid</code> from the response</li>
                  <li>Take the exact same JSON payload you sent to Meta</li>
                  <li>Add one extra field: <code className="bg-[#EFEFEF] px-1 rounded">"wamid": "&lt;the wamid from step 1&gt;"</code></li>
                  <li>POST it to the Webhook URL above — no auth header needed</li>
                </ol>
              </div>
              <div className="bg-[#F8F9FA] rounded-lg p-3 border border-[#D9E2E5]">
                <p className="text-[12px] font-semibold text-[#595C5C] uppercase mb-2">Example payload</p>
                <pre className="text-[11px] text-[#131517] overflow-x-auto whitespace-pre-wrap">{`{
  "messaging_product": "whatsapp",
  "to": "919XXXXXXXXX",
  "type": "template",
  "template": {
    "name": "your_template_name",
    "language": { "code": "en_US" },
    "components": [...]
  },
  "wamid": "wamid.HBgM..."
}`}</pre>
              </div>
              <p className="text-[12px] text-[#8A8A8B]">
                Works for all message types: <strong>text</strong>, <strong>template</strong>, <strong>image</strong>, <strong>video</strong>, <strong>audio</strong>, and <strong>document</strong>.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WhatsApp Configuration */}
      {client && (
        <Card className="border-[#D9E2E5]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[18px] font-semibold text-[#131517]">WhatsApp Configuration</CardTitle>
            <CardDescription className="text-[#8A8A8B]">Your WhatsApp Business Account settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">WABA ID</Label>
                <p className="text-[14px] text-[#131517] mt-1">{client.waba_id || <span className="text-[#AFB0B0]">Not configured</span>}</p>
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Phone Number ID</Label>
                <p className="text-[14px] text-[#131517] mt-1">{client.phone_number_id || <span className="text-[#AFB0B0]">Not configured</span>}</p>
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Phone Number</Label>
                <p className="text-[14px] text-[#131517] mt-1">{client.phone_number || <span className="text-[#AFB0B0]">Not configured</span>}</p>
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Business Name</Label>
                <p className="text-[14px] text-[#131517] mt-1">{client.business_name || <span className="text-[#AFB0B0]">Not configured</span>}</p>
              </div>
            </div>
            <p className="text-[12px] text-[#8A8A8B] mt-4">
              Contact your administrator to update WhatsApp configuration.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
